import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  LinearProgress,
} from "@mui/material";
import {
  AccountBalanceOutlined,
  InventoryOutlined,
  AssessmentOutlined,
  AttachMoneyOutlined,
  TrendingUpOutlined,
  TrendingDownOutlined,
  CheckCircleOutlined,
  PendingOutlined,
  WarningOutlined,
  ReceiptOutlined,
  AccountBalanceWalletOutlined,
  BusinessOutlined,
  NotificationsOutlined,
  RefreshOutlined,
  VisibilityOutlined,
  AddOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PrintOutlined,
} from "@mui/icons-material";
import { selectUser } from "../../../store/slices/authSlice";

// Import Finance APIs
import assetsAPI from "../../../services/api/assets.api";
import { budgetAPI } from "../../../services/api/budget.api";
import { pettyCashAPI } from "../../../services/api/pettyCash.api";
import { payrollAPI } from "../../../services/api/payroll.api";
import activityLogsAPI from "../../../services/api/activityLogs.api";

const FinanceManagerDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  // Fetch finance-specific data
  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ["assets", "finance-dashboard"],
    queryFn: () => assetsAPI.getStatistics(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    retryDelay: 1000,
  });

  const { data: budgetData, isLoading: budgetLoading } = useQuery({
    queryKey: ["budgets", "finance-dashboard"],
    queryFn: () => budgetAPI.getBudgets({ limit: 1000 }), // Get all budgets for proper counting
    staleTime: 5 * 60 * 1000,
  });

  const { data: pettyCashData, isLoading: pettyCashLoading } = useQuery({
    queryKey: ["petty-cash", "finance-dashboard"],
    queryFn: () => pettyCashAPI.getAll({ limit: 1000 }), // Get all petty cash requests
    staleTime: 5 * 60 * 1000,
    retry: 1,
    retryDelay: 1000,
  });

  const { data: payrollData, isLoading: payrollLoading } = useQuery({
    queryKey: ["payroll", "finance-dashboard"],
    queryFn: () => payrollAPI.getAll({ limit: 1000 }), // Get all payroll records
    staleTime: 5 * 60 * 1000,
  });

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: ["activities", "finance-dashboard"],
    queryFn: () => activityLogsAPI.getRecentActivities(10),
    staleTime: 1 * 60 * 1000,
  });

  // Calculate finance stats
  const totalAssets =
    assetsData?.data?.categoryStats?.reduce(
      (sum, category) => sum + (category.count || 0),
      0
    ) || 0;
  const totalAssetValue =
    assetsData?.data?.categoryStats?.reduce(
      (sum, category) => sum + (category.totalValue || 0),
      0
    ) || 0;
  const pendingBudgets =
    budgetData?.data?.budgets?.filter((item) => item.status === "pending")
      ?.length || 0;
  const totalBudgets =
    budgetData?.data?.budgets?.length ||
    budgetData?.data?.pagination?.totalItems ||
    0;
  const pendingPettyCash = pettyCashData?.data?.pendingReplenishment || 0;
  const totalPayroll =
    payrollData?.data?.records?.length ||
    payrollData?.data?.pagination?.totalItems ||
    0;
  const recentActivities = activitiesData?.data || [];

  const getUserGreeting = () => {
    const hour = new Date().getHours();
    let greeting = "Good morning";
    if (hour >= 12 && hour < 17) greeting = "Good afternoon";
    else if (hour >= 17) greeting = "Good evening";

    const firstName = user?.first_name || user?.name || "Finance Manager";
    return `${greeting}, ${firstName}!`;
  };

  const handleQuickAction = (actionType) => {
    switch (actionType) {
      case "assets":
        navigate("/finance/assets/register");
        break;
      case "budgets":
        navigate("/finance/budgets");
        break;
      case "budget-approvals":
        navigate("/finance/budgets/finance/approvals");
        break;
      case "petty-cash":
        navigate("/finance/petty-cash");
        break;
      case "payroll":
        navigate("/finance/payroll");
        break;
      case "reports":
        navigate("/reports");
        break;
      default:
        console.log("Action not defined:", actionType);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-TZ", {
      style: "currency",
      currency: "TZS",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const statsCards = [
    {
      title: "Total Assets",
      value: assetsLoading ? <CircularProgress size={20} /> : totalAssets,
      subtitle: formatCurrency(totalAssetValue),
      icon: <InventoryOutlined />,
      color: "primary",
    },
    {
      title: "Active Budgets",
      value: budgetLoading ? <CircularProgress size={20} /> : totalBudgets,
      subtitle: `${pendingBudgets} pending approval`,
      icon: <AccountBalanceOutlined />,
      color: "success",
    },
    {
      title: "Petty Cash",
      value: pettyCashLoading ? (
        <CircularProgress size={20} />
      ) : (
        pendingPettyCash
      ),
      subtitle: "pending approvals",
      icon: <AccountBalanceWalletOutlined />,
      color: "warning",
    },
    {
      title: "Payroll Records",
      value: payrollLoading ? <CircularProgress size={20} /> : totalPayroll,
      subtitle: "total processed",
      icon: <ReceiptOutlined />,
      color: "info",
    },
  ];

  const financeModules = [
    {
      title: "Asset Management",
      description: "Register and track organizational assets",
      icon: <InventoryOutlined />,
      color: "primary",
      stats: [
        { label: "Total Assets", value: totalAssets },
        { label: "Total Value", value: formatCurrency(totalAssetValue) },
      ],
      actions: [
        { label: "View Assets", action: () => handleQuickAction("assets") },
        {
          label: "Add Asset",
          action: () => navigate("/finance/assets/register?action=create"),
        },
      ],
    },
    {
      title: "Budget Management",
      description: "Create and manage departmental budgets",
      icon: <AccountBalanceOutlined />,
      color: "success",
      stats: [
        { label: "Active Budgets", value: totalBudgets },
        { label: "Pending Approval", value: pendingBudgets },
      ],
      actions: [
        { label: "View Budgets", action: () => handleQuickAction("budgets") },
        {
          label: "Approve Budgets",
          action: () => handleQuickAction("budget-approvals"),
        },
      ],
    },
    {
      title: "Petty Cash",
      description: "Manage petty cash and reimbursements",
      icon: <AccountBalanceWalletOutlined />,
      color: "warning",
      stats: [
        { label: "Pending Approvals", value: pendingPettyCash },
        { label: "Active Books", value: "3" },
      ],
      actions: [
        {
          label: "View Petty Cash",
          action: () => handleQuickAction("petty-cash"),
        },
        {
          label: "Create Entry",
          action: () => navigate("/finance/petty-cash/expenses"),
        },
      ],
    },
    {
      title: "Payroll Management",
      description: "Process and manage employee payroll",
      icon: <ReceiptOutlined />,
      color: "info",
      stats: [
        { label: "Total Records", value: totalPayroll },
        { label: "This Month", value: "45" },
      ],
      actions: [
        { label: "View Payroll", action: () => handleQuickAction("payroll") },
        {
          label: "Process Payroll",
          action: () => navigate("/finance/payroll/process"),
        },
      ],
    },
  ];

  return (
    <Box>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
          {getUserGreeting()}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome to the Finance Dashboard. Here's an overview of your financial
          operations.
        </Typography>
      </Box>

      {/* Key Financial Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${card.color}.main`,
                      width: 48,
                      height: 48,
                      mr: 2,
                    }}
                  >
                    {card.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.subtitle}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Finance Modules */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {financeModules.map((module, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${module.color}.main`,
                      width: 40,
                      height: 40,
                      mr: 2,
                    }}
                  >
                    {module.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {module.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {module.description}
                    </Typography>
                  </Box>
                </Box>

                {/* Module Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {module.stats.map((stat, statIndex) => (
                    <Grid item xs={6} key={statIndex}>
                      <Box>
                        <Typography variant="h6" color="primary">
                          {stat.value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {stat.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Module Actions */}
                <Stack direction="row" spacing={1}>
                  {module.actions.map((action, actionIndex) => (
                    <Button
                      key={actionIndex}
                      variant="outlined"
                      size="small"
                      onClick={action.action}
                      startIcon={
                        actionIndex === 0 ? (
                          <VisibilityOutlined />
                        ) : (
                          <AddOutlined />
                        )
                      }
                    >
                      {action.label}
                    </Button>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activity and Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Recent Finance Activities
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate("/settings/audit")}
                >
                  View All
                </Button>
              </Box>
              {activitiesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {recentActivities.slice(0, 5).map((activity, index) => (
                    <React.Fragment key={activity.id || index}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "success.main" }}>
                            <NotificationsOutlined />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={activity.action || "Finance Activity"}
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              {activity.description || "Activity performed"}
                              <br />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {new Date(activity.created_at).toLocaleString()}
                              </Typography>
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < recentActivities.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 3 }}>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<InventoryOutlined />}
                  onClick={() => handleQuickAction("assets")}
                  fullWidth
                >
                  Manage Assets
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AccountBalanceOutlined />}
                  onClick={() => handleQuickAction("budgets")}
                  fullWidth
                >
                  View Budgets
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AccountBalanceWalletOutlined />}
                  onClick={() => handleQuickAction("petty-cash")}
                  fullWidth
                >
                  Petty Cash
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ReceiptOutlined />}
                  onClick={() => handleQuickAction("payroll")}
                  fullWidth
                >
                  Process Payroll
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AssessmentOutlined />}
                  onClick={() => handleQuickAction("reports")}
                  fullWidth
                >
                  Financial Reports
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinanceManagerDashboard;
